/**
 * SelfHealingCard Component
 *
 * Displays self-healing system status including:
 * - Last run info and recovered items
 * - Current stuck counts (indexing jobs, NFTs, Redis)
 * - Manual trigger button
 */

import React, { useEffect, useState } from 'react';
import { getSelfHealingStats, triggerSelfHealing, type SelfHealingStats } from '../../services/adminApi.js';

export function SelfHealingCard() {
  const [stats, setStats] = useState<SelfHealingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState<string | null>(null);

  const loadStats = async () => {
    try {
      setError(null);
      const data = await getSelfHealingStats();
      setStats(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load self-healing stats');
    } finally {
      setLoading(false);
    }
  };

  const handleTrigger = async () => {
    try {
      setTriggering(true);
      setTriggerResult(null);
      const result = await triggerSelfHealing();
      setTriggerResult(`Triggered! Trace ID: ${result.traceId}`);
      // Reload stats after a short delay
      setTimeout(loadStats, 2000);
    } catch (err: any) {
      setTriggerResult(`Error: ${err.message}`);
    } finally {
      setTriggering(false);
    }
  };

  useEffect(() => {
    loadStats();
    // No auto-refresh - stats are loaded once on mount and after manual trigger
  }, []);

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Format count value - show "timeout" for -1 values (query timeout)
  const formatCount = (count: number): string => {
    if (count === -1) return 'timeout';
    return count.toLocaleString();
  };

  // Check if a count indicates an issue (positive count, but not timeout)
  const isIssue = (count: number): boolean => count > 0;

  // Check if any count is a timeout
  const hasTimeout = stats && (
    stats.currentStuckCounts.nfts.orphaned === -1 ||
    stats.currentStuckCounts.nfts.stuckMetadata === -1 ||
    stats.currentStuckCounts.nfts.stuckMetadataFetched === -1 ||
    stats.currentStuckCounts.nfts.stuckMedia === -1 ||
    stats.currentStuckCounts.nfts.noMetadataUri === -1
  );

  const hasIssues = stats && (
    stats.currentStuckCounts.indexingJobs > 0 ||
    isIssue(stats.currentStuckCounts.nfts.orphaned) ||
    isIssue(stats.currentStuckCounts.nfts.stuckMetadata) ||
    isIssue(stats.currentStuckCounts.nfts.stuckMetadataFetched) ||
    isIssue(stats.currentStuckCounts.nfts.stuckMedia) ||
    isIssue(stats.currentStuckCounts.nfts.noMetadataUri)
  );

  // Calculate total stuck (ignore timeout values)
  const safeCount = (c: number | undefined) => (c && c > 0) ? c : 0;
  const totalStuck = stats ? (
    stats.currentStuckCounts.indexingJobs +
    safeCount(stats.currentStuckCounts.nfts.orphaned) +
    safeCount(stats.currentStuckCounts.nfts.stuckMetadata) +
    safeCount(stats.currentStuckCounts.nfts.stuckMetadataFetched) +
    safeCount(stats.currentStuckCounts.nfts.stuckMedia) +
    safeCount(stats.currentStuckCounts.nfts.noMetadataUri)
  ) : 0;

  if (loading && !stats) {
    return (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-32"></div>
            <div className="h-8 bg-gray-200 rounded w-24"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Self-Healing Status</h3>
        <div className="text-red-600 text-sm">{error}</div>
        <button
          onClick={loadStats}
          className="mt-3 text-sm text-blue-600 hover:text-blue-800"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow p-4 sm:p-6 ${hasIssues ? 'border-l-4 border-yellow-500' : ''}`}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Self-Healing Status</h3>
          <p className="text-xs text-gray-500 mt-1">
            Automatically recovers stuck items every 15 minutes
          </p>
        </div>
        <button
          onClick={handleTrigger}
          disabled={triggering}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {triggering ? 'Triggering...' : 'Run Now'}
        </button>
      </div>

      {triggerResult && (
        <div className={`mb-4 p-2 rounded text-sm ${triggerResult.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {triggerResult}
        </div>
      )}

      {stats && (
        <>
          {/* Current Stuck Counts */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Current Stuck Items</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <div className={`p-2 rounded ${stats.currentStuckCounts.indexingJobs > 0 ? 'bg-yellow-50' : 'bg-gray-50'}`}>
                <div className="text-xs text-gray-500">Jobs</div>
                <div className={`text-lg font-bold ${stats.currentStuckCounts.indexingJobs > 0 ? 'text-yellow-700' : 'text-gray-900'}`}>
                  {stats.currentStuckCounts.indexingJobs}
                </div>
              </div>
              <div className={`p-2 rounded ${
                stats.currentStuckCounts.nfts.orphaned === -1 ? 'bg-orange-50' :
                stats.currentStuckCounts.nfts.orphaned > 0 ? 'bg-yellow-50' : 'bg-gray-50'
              }`}>
                <div className="text-xs text-gray-500">Orphaned</div>
                <div className={`text-lg font-bold ${
                  stats.currentStuckCounts.nfts.orphaned === -1 ? 'text-orange-600' :
                  stats.currentStuckCounts.nfts.orphaned > 0 ? 'text-yellow-700' : 'text-gray-900'
                }`}>
                  {formatCount(stats.currentStuckCounts.nfts.orphaned)}
                </div>
              </div>
              <div className={`p-2 rounded ${
                stats.currentStuckCounts.nfts.stuckMetadata === -1 ? 'bg-orange-50' :
                stats.currentStuckCounts.nfts.stuckMetadata > 0 ? 'bg-yellow-50' : 'bg-gray-50'
              }`}>
                <div className="text-xs text-gray-500">Metadata</div>
                <div className={`text-lg font-bold ${
                  stats.currentStuckCounts.nfts.stuckMetadata === -1 ? 'text-orange-600' :
                  stats.currentStuckCounts.nfts.stuckMetadata > 0 ? 'text-yellow-700' : 'text-gray-900'
                }`}>
                  {formatCount(stats.currentStuckCounts.nfts.stuckMetadata)}
                </div>
              </div>
              <div className={`p-2 rounded ${
                (stats.currentStuckCounts.nfts.stuckMetadataFetched ?? 0) === -1 ? 'bg-orange-50' :
                (stats.currentStuckCounts.nfts.stuckMetadataFetched ?? 0) > 0 ? 'bg-yellow-50' : 'bg-gray-50'
              }`}>
                <div className="text-xs text-gray-500">Meta-Fetch</div>
                <div className={`text-lg font-bold ${
                  (stats.currentStuckCounts.nfts.stuckMetadataFetched ?? 0) === -1 ? 'text-orange-600' :
                  (stats.currentStuckCounts.nfts.stuckMetadataFetched ?? 0) > 0 ? 'text-yellow-700' : 'text-gray-900'
                }`}>
                  {formatCount(stats.currentStuckCounts.nfts.stuckMetadataFetched ?? 0)}
                </div>
              </div>
              <div className={`p-2 rounded ${
                stats.currentStuckCounts.nfts.stuckMedia === -1 ? 'bg-orange-50' :
                stats.currentStuckCounts.nfts.stuckMedia > 0 ? 'bg-yellow-50' : 'bg-gray-50'
              }`}>
                <div className="text-xs text-gray-500">Media</div>
                <div className={`text-lg font-bold ${
                  stats.currentStuckCounts.nfts.stuckMedia === -1 ? 'text-orange-600' :
                  stats.currentStuckCounts.nfts.stuckMedia > 0 ? 'text-yellow-700' : 'text-gray-900'
                }`}>
                  {formatCount(stats.currentStuckCounts.nfts.stuckMedia)}
                </div>
              </div>
              <div className={`p-2 rounded ${
                (stats.currentStuckCounts.nfts.noMetadataUri ?? 0) === -1 ? 'bg-orange-50' :
                (stats.currentStuckCounts.nfts.noMetadataUri ?? 0) > 0 ? 'bg-yellow-50' : 'bg-gray-50'
              }`}>
                <div className="text-xs text-gray-500">No URI</div>
                <div className={`text-lg font-bold ${
                  (stats.currentStuckCounts.nfts.noMetadataUri ?? 0) === -1 ? 'text-orange-600' :
                  (stats.currentStuckCounts.nfts.noMetadataUri ?? 0) > 0 ? 'text-yellow-700' : 'text-gray-900'
                }`}>
                  {formatCount(stats.currentStuckCounts.nfts.noMetadataUri ?? 0)}
                </div>
              </div>
            </div>
            {hasTimeout && (
              <div className="mt-2 text-xs text-orange-600">
                Query timeout - database under heavy load. Counts may be inaccurate.
              </div>
            )}
          </div>

          {/* Last Run Info */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Last Run</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Time:</span>{' '}
                <span className="font-medium">{formatDate(stats.lastRun.at)}</span>
              </div>
              <div>
                <span className="text-gray-500">Duration:</span>{' '}
                <span className="font-medium">{stats.lastRun.durationMs}ms</span>
              </div>
              <div>
                <span className="text-gray-500">Errors:</span>{' '}
                <span className={`font-medium ${stats.lastRun.errors.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {stats.lastRun.errors.length}
                </span>
              </div>
            </div>

            {/* Recovered Items */}
            {stats.lastRun.at && (
              <div className="mt-3 grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-8 gap-2 text-xs">
                <div className="text-center p-1 bg-gray-50 rounded">
                  <div className="text-gray-500">Jobs</div>
                  <div className="font-medium">{stats.lastRun.recovered.indexingJobs}</div>
                </div>
                <div className="text-center p-1 bg-gray-50 rounded">
                  <div className="text-gray-500">Orphan</div>
                  <div className="font-medium">{stats.lastRun.recovered.orphanedNFTs}</div>
                </div>
                <div className="text-center p-1 bg-gray-50 rounded">
                  <div className="text-gray-500">Meta</div>
                  <div className="font-medium">{stats.lastRun.recovered.stuckMetadata}</div>
                </div>
                <div className="text-center p-1 bg-gray-50 rounded">
                  <div className="text-gray-500">M-Fetch</div>
                  <div className="font-medium">{stats.lastRun.recovered.stuckMetadataFetched || 0}</div>
                </div>
                <div className="text-center p-1 bg-gray-50 rounded">
                  <div className="text-gray-500">Media</div>
                  <div className="font-medium">{stats.lastRun.recovered.stuckMedia}</div>
                </div>
                <div className="text-center p-1 bg-gray-50 rounded">
                  <div className="text-gray-500">Dupes</div>
                  <div className="font-medium">{stats.lastRun.recovered.cleanedDuplicateJobs}</div>
                </div>
                <div className="text-center p-1 bg-gray-50 rounded">
                  <div className="text-gray-500">Failed</div>
                  <div className="font-medium">{stats.lastRun.recovered.markedPermanentFailures || 0}</div>
                </div>
                <div className="text-center p-1 bg-gray-50 rounded">
                  <div className="text-gray-500">NoURI</div>
                  <div className="font-medium">{stats.lastRun.recovered.markedNoMetadataUri || 0}</div>
                </div>
              </div>
            )}

            {/* Errors */}
            {stats.lastRun.errors.length > 0 && (
              <div className="mt-3 p-2 bg-red-50 rounded text-xs text-red-700">
                <div className="font-medium mb-1">Errors:</div>
                <ul className="list-disc list-inside">
                  {stats.lastRun.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Summary */}
          {hasIssues && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-sm font-medium text-yellow-800">
                  {totalStuck.toLocaleString()} items need attention
                </span>
              </div>
              <p className="text-xs text-yellow-700 mt-1">
                Click "Run Now" to trigger recovery.
              </p>
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-500">
        Runs automatically every 15 minutes. Click "Run Now" to trigger immediately.
      </div>
    </div>
  );
}
